import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { AvailableLanguages } from '@constants';
import { AllDeviceInfo, ConsumableCountry } from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { ReplaySubject } from 'rxjs';
import { DeviceInfoService } from '@core/services/device-info.service';

export interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-nationality-select',
  templateUrl: './nationality-select.component.html',
  styleUrls: ['./nationality-select.component.scss'],
  standalone: false,
})
export class NationalitySelectComponent implements OnInit, OnDestroy {
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfoService = inject(DeviceInfoService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() fieldControl: FormControl;
  @Input() fieldKey: string;
  @Input() fieldLabelKey: string;
  @Input() isRequired: boolean;
  // Array of pseudoId -> you can find those in api -> modules -> common -> countries.ts
  @Input() filterOutIds?: number[];
  @Input()
  public set minWidth(value: number | string | null | undefined) {
    this.minWidthCssVariable = this.normalizeCssSize(value);
  }

  public deviceInfo: AllDeviceInfo;
  public minWidthCssVariable: string | null = null;

  public nationalitiesList: SelectOption[];
  public currentLang: string;
  private rawCountries: ConsumableCountry[];

  private normalizeCssSize(value: number | string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return typeof value === 'number' ? `${value}px` : value;
  }

  public async ngOnInit(): Promise<void> {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.currentLang = this.translateService.currentLang;
    this.rawCountries = await this.commonsFacade
      .getCountries$()
      .pipe(
        take(1),
        map(countries => {
          if (this.filterOutIds?.length) {
            return countries.filter(country => !this.filterOutIds.includes(country.id));
          } else {
            return countries;
          }
        })
      )
      .toPromise();

    this.translateService.onLangChange
      .pipe(startWith({ lang: this.currentLang }), takeUntil(this.destroy$))
      .subscribe(({ lang: langKey }) => {
        const langKeyTyped = langKey as AvailableLanguages;
        const nationalities = this.rawCountries.map(consumableCountry => ({
          value: consumableCountry.nationality,
          label: consumableCountry.nationalityTranslations[langKeyTyped],
        }));
        nationalities.sort((a, b) => a.label.localeCompare(b.label));
        this.nationalitiesList = nationalities;
        this.currentLang = langKey;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
